import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  CaretLeft,
  CaretRight,
  CheckCircle,
  BookOpen,
  ChartLineUp,
  Trophy,
  Clock,
  Lightning,
  PaperPlaneTilt,
  SpeakerHigh,
  X,
  Brain,
  ArrowLeft,
  GraduationCap,
  Target,
  Scales,
  ArrowsClockwise
} from '@phosphor-icons/react';
import { INVESTORS_ACADEMY_RICH } from './courseDataRich';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Stock Ticker
const StockTicker = () => {
  const tickers = [
    { symbol: 'SPY', change: '+0.84%', up: true },
    { symbol: 'BRK.B', change: '+1.2%', up: true },
    { symbol: 'AAPL', change: '-0.34%', up: false },
    { symbol: 'MSFT', change: '+0.61%', up: true },
    { symbol: 'Gold', change: '+0.92%', up: true },
    { symbol: '10Y UST', change: '-0.12%', up: false },
    { symbol: 'BTC', change: '+2.1%', up: true },
    { symbol: 'VIX', change: '-3.2%', up: false },
  ];
  return (
    <div className="bg-zinc-900/80 border-b border-white/5 overflow-hidden">
      <div className="animate-ticker flex whitespace-nowrap py-2">
        {[...tickers, ...tickers].map((t, i) => (
          <span key={i} className="mx-6 text-xs font-mono">
            <span className="text-zinc-400">{t.symbol}</span>{' '}
            <span className={t.up ? 'text-green-400' : 'text-red-400'}>{t.change}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

// Floating Orb
const FloatingOrb = ({ onClick, isSpeaking, isOpen }) => (
  <motion.button
    data-testid="investor-orb-trigger"
    onClick={onClick}
    className={`fixed bottom-6 right-6 w-16 h-16 rounded-full cursor-pointer z-50 ${isSpeaking ? 'orb-speaking' : 'orb-idle'}`}
    style={{ background: 'radial-gradient(circle at 30% 30%, #e9d5a1, #c8a96e, #a67c3d)' }}
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.95 }}
  >
    {isOpen ? (
      <X size={24} weight="bold" className="text-zinc-900 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
    ) : (
      <span className="text-2xl absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"><ChartLineUp size={24} weight="bold" className="text-zinc-900" /></span>
    )}
  </motion.button>
);

// Chat Panel
const ChatPanel = ({ isOpen, messages, onSendMessage, isLoading, isSpeaking, onSpeak }) => {
  const [input, setInput] = useState('');
  const messagesEndRef = React.useRef(null);
  React.useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  const handleSubmit = (e) => { e.preventDefault(); if (input.trim() && !isLoading) { onSendMessage(input.trim()); setInput(''); } };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div data-testid="investor-chat-panel"
          initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.95 }}
          className="fixed bottom-24 right-6 w-96 max-w-[calc(100vw-48px)] h-[500px] bg-zinc-900 border border-white/10 rounded-lg shadow-2xl z-40 flex flex-col overflow-hidden">
          <div className="p-4 border-b border-white/10 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-200 to-amber-600 flex items-center justify-center">
              <ChartLineUp size={16} weight="bold" className="text-zinc-900" />
            </div>
            <div><h3 className="font-semibold text-white font-outfit">Investment Tutor</h3><p className="text-xs text-zinc-400">Powered by Claude</p></div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 && (<div className="text-center text-zinc-500 mt-8"><p className="text-sm">Hi! I'm your investment tutor.</p><p className="text-sm mt-2">Ask me about stocks, bonds, DCF, hedge funds, or anything investing!</p></div>)}
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] p-3 rounded-lg ${msg.role === 'user' ? 'bg-amber-500/20 text-amber-100' : 'bg-zinc-800 text-zinc-100'}`}>
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  {msg.role === 'assistant' && (<button onClick={() => onSpeak(msg.content)} className="mt-2 text-xs text-zinc-400 hover:text-amber-400 flex items-center gap-1"><SpeakerHigh size={14} /> Speak</button>)}
                </div>
              </div>
            ))}
            {isLoading && (<div className="flex justify-start"><div className="bg-zinc-800 p-3 rounded-lg flex gap-1"><span className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span><span className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span><span className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span></div></div>)}
            <div ref={messagesEndRef} />
          </div>
          <form onSubmit={handleSubmit} className="p-4 border-t border-white/10">
            <div className="flex gap-2">
              <input type="text" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about investing..." className="flex-1 bg-zinc-800 border border-white/10 rounded-lg px-4 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-amber-500/50" disabled={isLoading} />
              <button type="submit" disabled={isLoading || !input.trim()} className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white p-2 rounded-lg"><PaperPlaneTilt size={20} weight="fill" /></button>
            </div>
          </form>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Quiz Component
const QuizSection = ({ quiz }) => {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const handleSelect = (qIdx, oIdx) => {
    if (submitted) return;
    setAnswers(prev => ({ ...prev, [qIdx]: oIdx }));
  };

  const score = submitted ? quiz.filter((q, i) => answers[i] === q.correct).length : 0;

  return (
    <div data-testid="lesson-quiz-section">
      <h3 className="text-lg font-outfit font-semibold text-white mb-4 flex items-center gap-2">
        <Brain size={20} className="text-amber-400" />
        Knowledge Check
      </h3>
      <div className="space-y-5">
        {quiz.map((q, qIdx) => (
          <div key={qIdx} className="bg-zinc-800/50 rounded-lg p-4">
            <p className="text-sm font-medium text-white mb-3">{qIdx + 1}. {q.q}</p>
            <div className="space-y-2">
              {q.options.map((opt, oIdx) => {
                const isSelected = answers[qIdx] === oIdx;
                const isCorrect = submitted && oIdx === q.correct;
                const isWrong = submitted && isSelected && oIdx !== q.correct;
                return (
                  <button key={oIdx} onClick={() => handleSelect(qIdx, oIdx)}
                    data-testid={`quiz-q${qIdx}-opt${oIdx}`}
                    className={`w-full text-left p-3 rounded-lg text-sm transition-all border ${
                      isCorrect ? 'bg-green-500/20 border-green-500/50 text-green-300' :
                      isWrong ? 'bg-red-500/20 border-red-500/50 text-red-300' :
                      isSelected ? 'bg-amber-500/20 border-amber-500/50 text-amber-200' :
                      'bg-zinc-900/50 border-white/5 text-zinc-400 hover:border-white/20 hover:text-zinc-200'
                    }`}>
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {!submitted ? (
        <button onClick={() => setSubmitted(true)}
          disabled={Object.keys(answers).length < quiz.length}
          data-testid="quiz-submit-btn"
          className="mt-4 w-full bg-amber-600 hover:bg-amber-700 disabled:opacity-40 text-white py-3 rounded-lg font-semibold transition-colors">
          Check Answers ({Object.keys(answers).length}/{quiz.length})
        </button>
      ) : (
        <div className="mt-4 p-4 rounded-lg bg-zinc-800/50 border border-white/10 text-center">
          <p className="text-lg font-outfit font-bold text-white">{score}/{quiz.length} Correct</p>
          <p className="text-sm text-zinc-400 mt-1">{score === quiz.length ? 'Perfect score!' : score >= 2 ? 'Well done!' : 'Review the lesson and try again!'}</p>
          <button onClick={() => { setAnswers({}); setSubmitted(false); }} className="mt-3 text-sm text-amber-400 hover:text-amber-300">Retry Quiz</button>
        </div>
      )}
    </div>
  );
};

// Lesson Content View
const LessonView = ({ lesson, onBack, onAskTutor }) => (
  <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
    className="bg-zinc-900/50 border border-white/10 rounded-lg overflow-hidden" data-testid="lesson-detail-view">
    {/* Header */}
    <div className="p-5 md:p-6 border-b border-white/10">
      <button onClick={onBack} className="flex items-center gap-2 text-zinc-400 hover:text-white mb-4 transition-colors" data-testid="lesson-back-btn">
        <ArrowLeft size={18} /><span className="text-sm">Back to Phase</span>
      </button>
      <div className="flex items-start gap-4">
        <div className="text-4xl">{lesson.icon}</div>
        <div>
          <h2 className="text-xl md:text-2xl font-outfit font-bold text-white">{lesson.title}</h2>
          <p className="text-zinc-400 mt-1 text-sm md:text-base">{lesson.desc}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {lesson.tags.map((tag, i) => (<span key={i} className="text-xs px-2 py-1 bg-amber-500/10 text-amber-400 rounded">{tag}</span>))}
          </div>
        </div>
      </div>
    </div>

    {/* Content */}
    <div className="p-5 md:p-6 space-y-6">
      {/* Overview */}
      <div>
        <h3 className="text-lg font-outfit font-semibold text-white mb-3 flex items-center gap-2">
          <BookOpen size={20} className="text-amber-400" /> Overview
        </h3>
        <p className="text-zinc-300 leading-relaxed text-sm md:text-base">{lesson.content.overview}</p>
      </div>

      {/* Key Concepts */}
      <div>
        <h3 className="text-lg font-outfit font-semibold text-white mb-3 flex items-center gap-2">
          <Lightning size={20} className="text-amber-400" /> Key Concepts
        </h3>
        <ul className="space-y-2">
          {lesson.content.keyConcepts.map((c, i) => (
            <li key={i} className="flex items-start gap-3 text-zinc-300 text-sm md:text-base">
              <span className="text-amber-400 mt-1 flex-shrink-0">{'\u2022'}</span><span>{c}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Case Study */}
      {lesson.content.caseStudy && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-5">
          <h3 className="text-lg font-outfit font-semibold text-amber-400 mb-1 flex items-center gap-2">
            <Target size={20} /> Case Study
          </h3>
          <p className="text-xs text-amber-500/70 uppercase tracking-wider font-semibold mb-3">{lesson.content.caseStudy.title}</p>
          <p className="text-sm text-amber-200/80 italic mb-3">{lesson.content.caseStudy.subtitle}</p>
          <p className="text-sm text-zinc-300 leading-relaxed mb-4">{lesson.content.caseStudy.text}</p>
          <div className="grid grid-cols-2 gap-2">
            {lesson.content.caseStudy.stats.map((s, i) => (
              <div key={i} className="bg-zinc-900/50 rounded px-3 py-2 text-xs text-amber-300 font-mono">{s}</div>
            ))}
          </div>
        </div>
      )}

      {/* Key Takeaways */}
      <div>
        <h3 className="text-lg font-outfit font-semibold text-white mb-3 flex items-center gap-2">
          <Trophy size={20} className="text-amber-400" /> Key Takeaways
        </h3>
        <div className="grid md:grid-cols-2 gap-3">
          {lesson.content.keyTakeaways.map((t, i) => (
            <div key={i} className="bg-zinc-800/50 rounded-lg p-3 flex items-start gap-2">
              <CheckCircle size={18} className="text-green-400 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-zinc-300">{t}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Quiz */}
      {lesson.content.quiz && lesson.content.quiz.length > 0 && (
        <div className="border-t border-white/10 pt-6">
          <QuizSection quiz={lesson.content.quiz} />
        </div>
      )}

      {/* Ask Tutor CTA */}
      <div className="border-t border-white/10 pt-6">
        <button onClick={() => onAskTutor(`Help me understand: ${lesson.title}`)}
          data-testid="ask-tutor-btn"
          className="w-full bg-amber-600 hover:bg-amber-700 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors">
          <GraduationCap size={20} /> Ask AI Tutor About This Topic
        </button>
      </div>
    </div>
  </motion.div>
);

// Phase Card with clickable lessons
const PhaseCard = ({ phase, isExpanded, onToggle, onLessonClick }) => (
  <motion.div className="bg-zinc-900/50 border border-white/10 rounded-lg overflow-hidden" layout data-testid={`phase-card-${phase.id}`}>
    <button onClick={onToggle} data-testid={`phase-header-${phase.id}`}
      className="w-full p-4 md:p-5 flex items-center gap-4 hover:bg-zinc-800/30 transition-colors">
      <div className="w-12 h-12 rounded-lg flex items-center justify-center text-xl font-bold font-outfit"
        style={{ background: `${phase.color}20`, color: phase.color }}>
        {String(phase.id).padStart(2, '0')}
      </div>
      <div className="flex-1 text-left">
        <div className="text-xs text-zinc-500 font-medium">{phase.phase}</div>
        <h3 className="font-outfit font-semibold text-white mt-0.5">{phase.title}</h3>
        <div className="text-xs text-zinc-400 mt-1">{phase.duration} {'\u00B7'} {phase.topics.length} lessons</div>
      </div>
      <div className="text-right mr-2">
        <div className="text-lg font-outfit font-bold" style={{ color: phase.color }}>{phase.topics.length}</div>
        <div className="text-xs text-zinc-500">lessons</div>
      </div>
      <motion.div animate={{ rotate: isExpanded ? 90 : 0 }}>
        <CaretRight size={20} className="text-zinc-500" />
      </motion.div>
    </button>
    <AnimatePresence>
      {isExpanded && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
          <div className="px-4 md:px-5 pb-5 space-y-3">
            {phase.topics.map((topic, idx) => (
              <button key={idx} onClick={() => onLessonClick(phase.id, idx)}
                data-testid={`lesson-btn-${phase.id}-${idx}`}
                className="w-full flex items-start gap-3 p-3 bg-zinc-800/40 rounded-lg hover:bg-zinc-800/70 transition-colors text-left group">
                <span className="text-2xl">{topic.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-white text-sm group-hover:text-amber-400 transition-colors">{topic.title}</div>
                  <p className="text-xs text-zinc-400 mt-1 line-clamp-2">{topic.desc}</p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {topic.tags.map((tag, i) => (<span key={i} className="text-[10px] px-2 py-0.5 bg-zinc-700/50 text-zinc-400 rounded">{tag}</span>))}
                  </div>
                </div>
                <CaretRight size={18} className="text-zinc-500 group-hover:text-amber-400 mt-1" />
              </button>
            ))}
            <div className="pt-3 border-t border-white/5 flex items-center justify-between">
              <span className="text-xs text-zinc-500 font-mono">{phase.topics.length} lessons {'\u00B7'} AI tutor included</span>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

// Dalio Card
const DalioCard = ({ insight }) => (
  <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-5">
    <div className="text-3xl mb-3">{insight.icon}</div>
    <h3 className="font-outfit font-semibold text-white text-lg mb-2">{insight.title}</h3>
    <p className="text-sm text-zinc-400 leading-relaxed mb-4">{insight.text}</p>
    <ul className="space-y-2">
      {insight.principles.map((p, i) => (<li key={i} className="text-xs text-zinc-500 flex items-start gap-2"><span className="text-amber-500 mt-0.5">{'\u2022'}</span>{p}</li>))}
    </ul>
  </div>
);

// All Weather Portfolio
const AllWeatherPortfolio = ({ allocations }) => (
  <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-5">
    <div className="flex items-center gap-2 mb-4">
      <Scales size={24} className="text-amber-400" />
      <h3 className="font-outfit font-semibold text-white text-lg">The All Weather Portfolio</h3>
    </div>
    <div className="grid grid-cols-2 gap-3">
      {allocations.map((a, i) => (
        <div key={i} className="bg-zinc-800/50 rounded-lg p-3">
          <div className="text-2xl font-bold font-outfit" style={{ color: a.color }}>{a.allocation}%</div>
          <div className="text-sm font-medium text-white">{a.asset}</div>
          <div className="text-xs text-zinc-500 mt-1">{a.desc}</div>
        </div>
      ))}
    </div>
    <p className="text-xs text-zinc-500 italic mt-4">Designed for stability across all cycles. Rebalance annually.</p>
  </div>
);

// Main Dashboard
const Dashboard = ({ onBack }) => {
  const [expandedPhase, setExpandedPhase] = useState(1);
  const [activeTab, setActiveTab] = useState('curriculum');
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [audioRef] = useState(() => new Audio());

  const course = INVESTORS_ACADEMY_RICH;

  const handleSendMessage = useCallback(async (message) => {
    setChatMessages(prev => [...prev, { role: 'user', content: message }]);
    setChatLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, { message, context: 'investors-academy' });
      setChatMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (error) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
    } finally { setChatLoading(false); }
  }, []);

  const handleSpeak = useCallback(async (text) => {
    if (isSpeaking) { audioRef.pause(); setIsSpeaking(false); return; }
    try {
      setIsSpeaking(true);
      const res = await axios.post(`${API}/tts`, { text: text.slice(0, 4096), voice: 'onyx' });
      audioRef.src = `data:audio/mp3;base64,${res.data.audio}`;
      audioRef.onended = () => setIsSpeaking(false);
      await audioRef.play();
    } catch (error) { setIsSpeaking(false); }
  }, [isSpeaking, audioRef]);

  const handleLessonClick = (phaseId, lessonIdx) => {
    const phase = course.curriculum.find(p => p.id === phaseId);
    if (phase && phase.topics[lessonIdx]) {
      setSelectedLesson({ phaseId, lessonIdx, lesson: phase.topics[lessonIdx] });
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="investors-academy-dashboard">
      <StockTicker />
      <main className="p-4 md:p-6 lg:p-8 overflow-auto">
        <div className="max-w-5xl mx-auto">
          {onBack && (
            <button onClick={onBack} className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors" data-testid="back-to-courses">
              <CaretLeft size={20} /><span className="text-sm">Back to Courses</span>
            </button>
          )}

          {/* Hero */}
          <header className="mb-8 text-center">
            <p className="text-xs text-amber-500 uppercase tracking-[0.2em] font-semibold">StudyCoach Academy {'\u2014'} Investing Track</p>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-outfit font-bold text-white mt-3">
              From First Dollar<br/>to <em className="text-amber-400 italic">Investment Firm</em>
            </h1>
            <p className="text-zinc-400 mt-4 max-w-xl mx-auto text-sm md:text-base">{course.description}</p>
            <div className="flex justify-center gap-6 md:gap-10 mt-8 flex-wrap">
              {[
                { val: course.stats.phases, label: 'Learning Phases' },
                { val: course.stats.lessons, label: 'Lessons' },
                { val: course.stats.yearsToFirm, label: 'Years to Firm' },
                { val: course.stats.returnPotential, label: 'Return Potential' }
              ].map((s, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl md:text-3xl font-outfit font-bold text-amber-400">{s.val}</div>
                  <div className="text-xs text-zinc-500 uppercase tracking-wider mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </header>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {[
              { id: 'curriculum', label: 'Curriculum', icon: BookOpen },
              { id: 'dalio', label: 'Ray Dalio', icon: Brain },
              { id: 'glossary', label: 'Glossary', icon: BookOpen }
            ].map(tab => (
              <button key={tab.id}
                onClick={() => { setActiveTab(tab.id); setSelectedLesson(null); }}
                data-testid={`tab-${tab.id}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${
                  activeTab === tab.id ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
                }`}>
                <tab.icon size={18} />{tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <AnimatePresence mode="wait">
            {activeTab === 'curriculum' && !selectedLesson && (
              <motion.section key="curriculum" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                <div className="mb-6">
                  <span className="text-xs text-amber-500 uppercase tracking-widest font-semibold">The Full Curriculum</span>
                  <h2 className="text-2xl font-outfit font-bold text-white mt-1">Five Phases to Mastery</h2>
                  <p className="text-sm text-zinc-400 mt-2">Click each phase to expand, then click a lesson to read the full content.</p>
                </div>
                <div className="space-y-3">
                  {course.curriculum.map(phase => (
                    <PhaseCard key={phase.id} phase={phase}
                      isExpanded={expandedPhase === phase.id}
                      onToggle={() => setExpandedPhase(expandedPhase === phase.id ? null : phase.id)}
                      onLessonClick={handleLessonClick} />
                  ))}
                </div>
              </motion.section>
            )}

            {activeTab === 'curriculum' && selectedLesson && (
              <LessonView key="lesson" lesson={selectedLesson.lesson}
                onBack={() => setSelectedLesson(null)}
                onAskTutor={(q) => { setChatOpen(true); handleSendMessage(q); }} />
            )}

            {activeTab === 'dalio' && (
              <motion.section key="dalio" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                <div className="mb-6">
                  <span className="text-xs text-amber-500 uppercase tracking-widest font-semibold">Master Class</span>
                  <h2 className="text-2xl font-outfit font-bold text-white mt-1">What Ray Dalio Knows</h2>
                  <p className="text-sm text-zinc-400 mt-2">Dalio built Bridgewater Associates into the world's largest hedge fund ($150B+ AUM) using these principles.</p>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {course.dalioInsights.map((insight, i) => (<DalioCard key={i} insight={insight} />))}
                  <div className="md:col-span-2"><AllWeatherPortfolio allocations={course.allWeatherPortfolio} /></div>
                </div>
              </motion.section>
            )}

            {activeTab === 'glossary' && (
              <motion.section key="glossary" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                <div className="mb-6">
                  <span className="text-xs text-amber-500 uppercase tracking-widest font-semibold">Key Terms</span>
                  <h2 className="text-2xl font-outfit font-bold text-white mt-1">The Investor's Glossary</h2>
                  <p className="text-sm text-zinc-400 mt-2">Master these terms and you'll understand 95% of any financial conversation.</p>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  {course.glossary.map((item, i) => (
                    <div key={i} className="bg-zinc-900/50 border border-white/10 rounded-lg p-4">
                      <div className="font-semibold text-amber-400 font-mono text-sm">{item.term}</div>
                      <p className="text-sm text-zinc-400 mt-1">{item.def}</p>
                    </div>
                  ))}
                </div>
              </motion.section>
            )}
          </AnimatePresence>
        </div>
      </main>

      <FloatingOrb onClick={() => setChatOpen(!chatOpen)} isSpeaking={isSpeaking} isOpen={chatOpen} />
      <ChatPanel isOpen={chatOpen} messages={chatMessages} onSendMessage={handleSendMessage} isLoading={chatLoading} isSpeaking={isSpeaking} onSpeak={handleSpeak} />
    </div>
  );
};

export default Dashboard;
